import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { KatDropdown, KatList } from '@amzn/katal-react';
import i18n from '../i18n';
import { AuthenticationState } from './hooks/useTrimContext';

interface NavHeaderProps {
  authenticationState: AuthenticationState;
  currentLocale: string;
  isDisputeEnabled: boolean;
  isRetractEnabled: boolean;
  supportedLanguages: string[];
}

const NavHeader: React.FC<NavHeaderProps> = (props: NavHeaderProps) => {
  const { t } = useTranslation(['pnf_header_footer', 'pnf_language_selection']);
  const {
    authenticationState,
    currentLocale,
    isDisputeEnabled,
    isRetractEnabled,
    supportedLanguages,
  } = props;
  const [isHover, setIsHover] = React.useState(false);

  useEffect(() => {
    i18n.changeLanguage(currentLocale.replace('_', '-'));
  }, []);

  const onQuickLinkSettingsMouseOver = (): void => {
    setIsHover(true);
  };

  const onQuickLinkSettingsMouseOut = (): void => {
    setIsHover(false);
  };

  const quickLinkSettingsCss = isHover ? 'sc-active' : '';
  const quickLinkSettingsOptionsCss = isHover ? 'd-block' : 'd-none';

  const onChangeLanguageSwitcher = (e: CustomEvent<{ value: string }>) => {
    const language: string = e.detail.value;
    if (language) {
      i18n.changeLanguage(language.replace('_', '-'))
        .then(() => {
          document.dir = i18n.dir();
        });
    }
  };

  const loginLogoutLink = authenticationState.authenticated
    ? (
      <a href={`${authenticationState.signOutUrl}`} id="quicklink-logout">
        {t('pnf_nav_header_logout')}
      </a>
    )
    : (
      <a href={`${authenticationState.signInUrl}`} id="quicklink-signin">
        {t('pnf_nav_header_sign-in')}
      </a>
    );

  const languageOptions = supportedLanguages.map((language) =>
    ({
      name: t(`pnf_sc_language_selection_${language}`),
      value: language,
    })
  );

  return (
    <div id="sc-navbar-container">
      <div className="clearable" id="sc-masthead">
        <div id="sc-logo-top">
          <a href="/" title="Home">
            <div className="rainier-nav-brand_registry_logo-01" id="sc-logo-asset" />
          </a>
        </div>
        <div>
          <div id="sc-navbar-formlinks">
            <ul id="sc-quicklinks">
              <li className="sc-quicklink" data-feature-name="report" id="sc-quicklink-help">
                <a className="sc-ql-a" href="/report/infringement">
                  <span className="sc-navbar-txt" id="report-form-link">
                    {t('public-notice-form-header')}
                  </span>
                </a>
              </li>
              {isRetractEnabled &&
                <li className="sc-quicklink" data-feature-name="retract" id="sc-quicklink-help">
                  <a className="sc-ql-a" href="/report/infringement/retract">
                    <span className="sc-navbar-txt" id="retract-form-link">
                      {t('pnf-retract-form-title')}
                    </span>
                  </a>
                </li>
              }
              {isDisputeEnabled &&
              <li className="sc-quicklink" data-feature-name="dispute" id="sc-quicklink-help">
                <a className="sc-ql-a" href="/report/dispute">
                  <span className="sc-navbar-txt" id="dispute-form-link">
                    {t('pnf-dispute-form-title')}
                  </span>
                </a>
              </li>
              }
            </ul>
          </div>
        </div>
        <div id="sc-quick-search-switch">
          <div id="sc-quicklinks-top">
            <ul id="sc-quicklinks">
              <li className="sc-quicklink" data-feature-name="scHelpLink" id="sc-quicklink-help">
                <a className="sc-ql-a" href="/gp/help/customer/display.html?nodeId=508510&ref_=nav_cs_help">
                  <span className="sc-quick-txt">
                    {t('pnf_nav_header_help')}
                  </span>
                  <span className="sc-mobile-icon" title="Help" />
                  <span className="sc-nav-arrow" />
                </a>
              </li>
              {/* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */}
              <li
                className={`sc-quicklink sc-drop-nav sc-hover-nav ${quickLinkSettingsCss}`}
                id="sc-quicklink-settings"
                onMouseOut={onQuickLinkSettingsMouseOut}
                onMouseOver={onQuickLinkSettingsMouseOver}
              >
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a className="sc-menu-trigger sc-ql-a" href="#">
                  <span className="sc-quick-txt">
                    {t('pnf_nav_header_your-account')}
                  </span>
                  <span className="sc-mobile-icon" title="Settings" />
                  <span className="sc-nav-arrow" />
                </a>
                {/* eslint-disable-next-line max-len */}
                <KatList className={`sc-sub-nav ${quickLinkSettingsOptionsCss}`} id="sc-sub-nav-ul-settings" variant="unordered">
                  <li className="sc-logout-quicklink">
                    <span>
                      {loginLogoutLink}
                    </span>
                  </li>
                  <li className="sc-loginsett-quicklink">
                    <span>
                      <a href="/gp/css/homepage.html?ie=UTF8&ref_=nav_youraccount_ya">
                        {t('pnf_nav_header_your-account')}
                      </a>
                    </span>
                  </li>
                </KatList>
              </li>
            </ul>
          </div>
          <div className="sc-drop-nav" id="sc-lang-switcher-header">
            <KatDropdown
              id="language-switcher"
              options={languageOptions}
              size="small"
              value={currentLocale}
              onChange={onChangeLanguageSwitcher}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavHeader;
